import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  CatalogEditorSheet,
  Checkbox,
  CommerceCatalogWorkspace,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  VerticalStack,
  toast,
  type CatalogEditorFormValue,
  type CatalogEditorViewModel,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';

import {
  archiveCatalog,
  createCatalog,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listCatalogs,
  updateCatalog,
  type Catalog,
  type CommerceFacetBucket,
} from '@/lib/commerce';
import { buildCatalogRoute } from '@/lib/commerce-routing';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery, withCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  catalogToForm,
  CommerceBreadcrumbHeader,
  emptyCatalogForm,
  formatRelativeTime,
  type EditorMode,
  metadataEntriesToRecord,
  type ValidationMap,
  validateCatalogForm,
} from './catalogHierarchyShared';

const CATALOG_SORT_FIELDS = [
  'name',
  'catalog_key',
  'status',
  'default_currency',
  'products',
  'created_at',
  'updated_at',
] as const;

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'published':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

export default function CatalogsPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();

  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [totalCatalogs, setTotalCatalogs] = useState(0);
  const [statusFacets, setStatusFacets] = useState<CommerceFacetBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['draft', 'active', 'archived'] },
    sortFields: CATALOG_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const requestSequence = useRef(0);

  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCatalogId, setEditingCatalogId] = useState<string | null>(null);
  const [catalogForm, setCatalogForm] = useState<CatalogEditorFormValue>(emptyCatalogForm);
  const [validation, setValidation] = useState<ValidationMap>({});
  const [saving, setSaving] = useState(false);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const columnFilters = useMemo<ColumnFiltersState>(() =>
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : [],
  [query.facets.status]);

  const columns = useMemo<ColumnDef<Catalog>[]>(() => defineCommerceRegistryColumns<Catalog>([
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      size: 300,
      header: 'Name',
      cell: ({ row }) => {
        const catalog = row.original;
        return (
          <button
            type="button"
            onClick={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(catalog.catalog_id), navigation.path))}
            className="group min-w-0 text-left"
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {catalog.name}
            </p>
            {catalog.description && (
              <DescriptionText className="line-clamp-1 text-[11px]">
                {catalog.description}
              </DescriptionText>
            )}
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id))
      },
    },
    {
      accessorKey: 'catalog_key',
      size: 200,
      header: 'Key',
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">{row.original.catalog_key}</span>
      ),
    },
    {
      id: 'default_currency',
      accessorFn: row => row.default_currency || 'USD',
      size: 80,
      header: 'Currency',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.default_currency || 'USD'}</span>
      ),
    },
    {
      id: 'products',
      accessorFn: row => Number(row.metadata?.product_count ?? 0),
      size: 100,
      header: 'Products',
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">
          {String(row.original.metadata?.product_count ?? 0)}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 120,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatRelativeTime(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 80,
      enableSorting: false,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const catalog = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => openEdit(catalog)}
              aria-label={`Edit ${catalog.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => void deleteCatalogRecord(catalog)}
              aria-label={`Delete ${catalog.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:text-destructive hover:bg-destructive/5"
            >
              <Trash2 className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(catalog.catalog_id), navigation.path))}
              aria-label={`View products in ${catalog.name}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-foreground transition-all hover:bg-muted"
            >
              Products
              <ArrowRight className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], {
    mode: 'manual',
    registry: 'catalogs',
    sortFields: CATALOG_SORT_FIELDS,
  }), [navigation]);

  const normalizedSearch = searchQuery.trim();

  const loadCatalogs = async () => {
    const requestID = ++requestSequence.current;
    if (!activeTenant?.id) {
      setCatalogs([]);
      setTotalCatalogs(0);
      setStatusFacets([]);
      setError(null);
      setAccessDeniedStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);

    try {
      const page = await listCatalogs({
        catalogOwnerTenantId: activeTenant.id,
        ...toLimitOffset(query.pagination),
        ...(normalizedSearch ? { search: normalizedSearch } : {}),
        ...(query.facets.status.length > 0 ? { status: query.facets.status } : {}),
        ...(query.sort.length > 0 ? { sort: query.sort } : {}),
        include_facets: ['status'],
      });
      if (requestID !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(page.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setCatalogs(page.items);
      setTotalCatalogs(page.total_count);
      setStatusFacets(page.facets.status ?? []);
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load catalogs.'));
      }
      setCatalogs([]);
      setTotalCatalogs(0);
      setStatusFacets([]);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadCatalogs();
  }, [
    activeTenant?.id,
    normalizedSearch,
    query.facets.status.join('\u0000'),
    query.pagination.pageIndex,
    query.pagination.pageSize,
    query.sort.join('\u0000'),
  ]);

  const handleSearchChange = (value: string) => {
    query.setSearch(value);
    setRowSelection({});
  };

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    const next = typeof updater === 'function' ? updater(columnFilters) : updater;
    const statusFilter = next.find(filter => filter.id === 'status');
    query.setFacet('status', Array.isArray(statusFilter?.value) ? statusFilter.value.map(String) : []);
  };

  const openCreate = () => {
    setEditorMode('create');
    setEditingCatalogId(null);
    setCatalogForm(emptyCatalogForm());
    setValidation({});
    setEditorOpen(true);
  };

  const openEdit = (catalog: Catalog) => {
    setEditorMode('edit');
    setEditingCatalogId(catalog.catalog_id);
    setCatalogForm(catalogToForm(catalog));
    setValidation({});
    setEditorOpen(true);
  };

  const saveCatalog = async () => {
    if (!activeTenant?.id) {
      return;
    }

    const nextValidation = validateCatalogForm(catalogForm);
    setValidation(nextValidation);
    if (Object.keys(nextValidation).length > 0) {
      return;
    }

    const payload = {
      catalog_owner_tenant_id: activeTenant.id,
      catalog_key: catalogForm.identity.catalogKey.trim(),
      name: catalogForm.identity.name.trim(),
      status: catalogForm.lifecycle.status,
      default_currency: catalogForm.lifecycle.defaultCurrency || '',
      description: catalogForm.description || '',
      metadata: metadataEntriesToRecord(catalogForm.metadata),
    };

    setSaving(true);
    setError(null);
    try {
      const saved =
        editorMode === 'edit' && editingCatalogId
          ? await updateCatalog(editingCatalogId, payload)
          : await createCatalog(payload);
      await loadCatalogs();
      setEditorOpen(false);
      toast.success(editorMode === 'edit' ? 'Catalog updated' : 'Catalog created', {
        description: saved.name,
      });
      navigation.navigate(withCommerceRegistryQuery(buildCatalogRoute(saved.catalog_id), navigation.path));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to save catalog.');
        setError(msg);
        toast.error('Failed to save catalog', { description: msg });
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteCatalogRecord = async (catalog: Catalog) => {
    if (!activeTenant?.id) {
      return;
    }
    if (!window.confirm(`Delete catalog "${catalog.name}"?`)) {
      return;
    }

    setError(null);
    try {
      await archiveCatalog(catalog.catalog_id);
      await loadCatalogs();
      toast.success('Catalog deleted', {
        description: catalog.name,
      });
      if (editingCatalogId === catalog.catalog_id) {
        setEditorOpen(false);
        setEditingCatalogId(null);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete catalog.');
        setError(msg);
        toast.error('Failed to delete catalog', { description: msg });
      }
    }
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    searchQuery,
    onSearchChange: handleSearchChange,
    error,
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'catalog-create',
      label: 'New Catalog',
      onAction: activeTenant?.id ? openCreate : undefined,
      disabled: !activeTenant?.id,
    },
    metrics: [
      {
        id: 'catalog-total',
        label: 'Total Catalogs',
        value: String(catalogs.length),
        helperText: 'Primary product libraries owned by this tenant.',
      },
      {
        id: 'catalog-active',
        label: 'Active Catalogs',
        value: String(catalogs.filter(catalog => catalog.status.toLowerCase() === 'active').length),
        tone: 'success',
        helperText: 'Available for publication and pricing.',
      },
      {
        id: 'catalog-draft',
        label: 'Draft Mode',
        value: String(catalogs.filter(catalog => catalog.status.toLowerCase() === 'draft').length),
        tone: 'warning',
        helperText: 'Work-in-progress libraries.',
      },
    ],
  });

  const editorViewModel: CatalogEditorViewModel = {
    mode: editorMode,
    state: saving ? 'saving' : 'ready',
    isOpen: editorOpen,
    title: editorMode === 'edit' ? 'Edit Catalog' : 'Create Catalog',
    subtitle:
      editorMode === 'edit'
        ? 'Refine the catalog identity and publishing defaults.'
        : 'Create a new catalog library for this tenant.',
    saveActionLabel: editorMode === 'edit' ? 'Save Catalog' : 'Create Catalog',
    catalogId: editingCatalogId,
    value: catalogForm,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    currencyOptions: [
      { value: 'USD', label: 'USD' },
      { value: 'CAD', label: 'CAD' },
      { value: 'EUR', label: 'EUR' },
    ],
    metrics:
      editorMode === 'edit' && editingCatalogId
        ? [
            {
              id: 'catalog-products',
              label: 'Products',
              value: String(catalogs.find(catalog => catalog.catalog_id === editingCatalogId)?.metadata?.product_count || 0),
            },
            {
              id: 'catalog-status',
              label: 'Status',
              value: catalogForm.lifecycle.status || 'draft',
            },
          ]
        : undefined,
    fieldHelp: [],
    validationErrors: validation,
    onChange: patch => setCatalogForm(current => ({ ...current, ...patch })),
    onSave: saveCatalog,
    onClose: () => setEditorOpen(false),
  };

  return (
    <>
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[{ label: 'Catalogs' }]}
            title="Catalogs"
            description="Manage the catalog libraries owned by the current tenant."
          />
        }
      >
        <RegistryWorkspace>
          <RegistryTableSurface>
            <DataTable
              mode="manual"
              columns={columns}
              data={catalogs}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              columnFilters={columnFilters}
              onColumnFiltersChange={handleColumnFiltersChange}
              globalFilter={searchQuery}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              pagination={query.pagination}
              onPaginationChange={query.onPaginationChange}
              totalRowCount={totalCatalogs}
              loading={loading}
              className="border-none rounded-none"
              paginationClassName="px-0 py-0"
              showPagination={true}
              emptyState={
                <div className="px-0 py-12 text-center">
                  <p className="text-sm font-black text-foreground">No catalogs found</p>
                  <DescriptionText className="mt-1 text-xs">
                    {normalizedSearch
                      ? 'No catalogs matched the current search.'
                      : 'Create the first catalog to start organizing products for storefront and reseller distribution.'}
                  </DescriptionText>
                </div>
              }
              renderToolbar={(table) => (
                <DataTableToolbar
                  table={table}
                  searchValue={searchQuery}
                  onSearchChange={handleSearchChange}
                  searchPlaceholder="Search all catalogs..."
                  resultCount={totalCatalogs}
                  className="border-b border-border/40 bg-transparent px-0 py-0"
                  filters={
                    table.getColumn('status') && (
                      <DataTableFacetedFilter
                        title="Status"
                        selectedValues={new Set(table.getColumn('status')?.getFilterValue() as string[] ?? [])}
                        onFilterChange={(values) => table.getColumn('status')?.setFilterValue(Array.from(values))}
                        options={[
                          { label: statusFacets.find(bucket => bucket.value === 'draft')?.label || 'Draft', value: 'draft' },
                          { label: statusFacets.find(bucket => bucket.value === 'active')?.label || 'Active', value: 'active' },
                          { label: statusFacets.find(bucket => bucket.value === 'archived')?.label || 'Archived', value: 'archived' },
                        ]}
                        counts={Object.fromEntries(statusFacets.map(bucket => [bucket.value, bucket.count]))}
                      />
                    )
                  }
                />
              )}
            />
          </RegistryTableSurface>
        </RegistryWorkspace>
      </CommerceCatalogWorkspace>

      <CatalogEditorSheet editor={editorViewModel} />
    </>
  );
}
